import React, { useContext, useEffect, useState } from 'react'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as EditIcon } from 'presentation/assets/icons/pencil-new.svg'
import { ReactComponent as ArrowIcon } from 'presentation/assets/icons/close-arrow.svg'
import { ReactComponent as CheckCircle } from 'presentation/assets/icons/check-circle.svg'
import { ReactComponent as AlertCircle } from 'presentation/assets/icons/alert-circle.svg'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import CardNew from 'presentation/shared/components/CardNew'
import {
  AmbulatoryAuthorizationDashboardContext,
  Procedure,
  Cid,
  Medication,
  Material,
  DocumentFile
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import { formatCpf } from 'common/utils/format-cpf'
import moment from 'moment-timezone'
import { formatPhone } from 'presentation/utils/masks'
import { ProcedureCard } from 'presentation/doctor/components/Cards/AmbulatoryProcedureCard'
import * as S from './styles'
import { CidCard } from 'presentation/doctor/components/Cards/AmbulatoryCidCard'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import { MedicationCard } from 'presentation/doctor/components/Cards/AmbulatoryMedicationCard'
import { MaterialCard } from 'presentation/doctor/components/Cards/AmbulatoryMaterialCard'
import AccordionCardNew from 'presentation/shared/components/AccordionCardNew'
import MultimediaFileInputNew from 'presentation/shared/components/MultimediaFileInputNew'
import { documentList } from '../AmbulatoryDocuments/documentList'
import { toast } from 'react-toastify'
import { useServices } from 'presentation/hooks/use-services'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { renameFile } from 'common/utils/file/renameFile'
import { AmbulatoryDoctorDocument } from 'common/enum/ambulatory-doctor-document'

const AmbulatoryResumeLayout = WithLoading(
  ({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const documentService = useServices().document
    const ambulatoryService = useServices().ambulatory
    const { state, dispatch } = useContext<any>(
      AmbulatoryAuthorizationDashboardContext
    )
    const {
      doctor,
      patient,
      unit,
      health_insurance_info,
      procedures,
      cids,
      medications,
      materials,
      observation,
      documents
    } = state

    const [informationAccordionOpen, setInformationAccordionOpen] = useState('')

    if (!patient?.cpf) {
      history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
    }

    const handleSubmit = async () => {
      if (!documentsIsValid()) {
        toast.error('Adicione documentos obrigatórios')
        return
      }

      const payload = {
        doctor: {
          doctorId: doctor?.doctor_id,
          name: doctor?.name
        },
        patient: {
          patientId: patient?.patient_id,
          name: patient?.name
        },
        hospital: {
          hospitalId: unit?.hospital_id,
          name: unit?.friendly_name || unit?.name
        },
        healthInsurance: {
          code: health_insurance_info?.health_insurance_code,
          name: health_insurance_info?.health_insurance_name,
          company: health_insurance_info?.company_name,
          healthCard: health_insurance_info?.health_card,
          healthPlanCode: health_insurance_info?.health_plan_code,
          healthPlanName: health_insurance_info?.health_plan_name,
          healthSubPlanCode: health_insurance_info?.health_sub_plan_code,
          healthSubPlanName: health_insurance_info?.health_sub_plan_name,
          validThru: health_insurance_info?.valid_thru
        },
        procedures: procedures,
        cids: cids,
        medications: medications,
        materials: materials,
        observation: observation,
        createdBy: doctor?.doctor_id,
        createdByName: doctor?.name,
        documents: [] as string[]
      }

      try {
        setIsLoading(true)
        const documentIds = documents
          .map((doc: DocumentFile) => doc.document_id)
          .filter((id: string) => !!id)
        const uploadedDocumentIds = await awsConnectionAndUploadFiles()

        payload.documents = [
          ...documentIds,
          ...(uploadedDocumentIds || [])
        ].flat()

        const ambulatoryOrderResult =
          await ambulatoryService.createAmbulatoryOrder(payload)

        toast.success(
          <>
            <HeadingNew color="neutral800" size="small">
              Pedido enviado com sucesso!
            </HeadingNew>
            <SupportTextNew
              size="xsmall"
              style={{ marginTop: '4px', lineHeight: 1.3 }}
            >
              O hospital analisará as informações para iniciar o processo de
              autorização.
            </SupportTextNew>
          </>,
          {
            position: 'top-center',
            autoClose: false,
            bodyStyle: {
              width: '500px',
              padding: '8px 8px 16px'
            }
          }
        )

        history.push('/pedidos-autorizacao-ambulatorial/detalhes', {
          orderId: ambulatoryOrderResult?.data?.id
        })

        // Reset context state
        dispatch({ type: 'RESET_AMBULATORY_STATE', payload: null })
      } catch (error: any) {
        toast.error(error?.message)
      } finally {
        setIsLoading(false)
      }
    }

    const documentsIsValid = () => {
      const requiredDocsLength = documentList.filter(
        (document: DocumentFile) => document.required
      )?.length
      const selectedRequiredDocsLength = state.documents?.filter(
        (doc: DocumentFile) => doc.required
      )?.length

      return requiredDocsLength === selectedRequiredDocsLength
    }

    const awsConnectionAndUploadFiles = async () => {
      // Filters documents added by the user
      const filterDocumentsToUpload = documents.filter(
        (doc: DocumentFile) => !!doc.file?.name
      )

      if (!filterDocumentsToUpload?.length) return

      const uploadedDocIds: string[] = []

      await Promise.all(
        filterDocumentsToUpload.map(async ({ file, type }: DocumentFile) => {
          const formattedDocumentName = AmbulatoryDoctorDocument[type]
            ?.toLowerCase()
            ?.normalize('NFD')
            ?.replace(/[^a-zA-Z\s]/g, '')
            ?.replace(/\s+/g, '-')

          const renamedFile = renameFile(file!, formattedDocumentName, {
            forceOriginalExtension: true
          })

          const toastFile = toast.loading(
            `Enviando ${AmbulatoryDoctorDocument[type]}`
          )

          try {
            // Request pre signed url:
            const preSignedUrlResponse =
              await documentService.generateAwsPresignedUrlDocument({
                document_name: renamedFile?.name,
                document_type: renamedFile?.type ?? '',
                folder_name: String(patient?.patient_id) || null
              })

            // Make upload file:
            await documentService.uploadDocumentAws({
              preSignedUrl: preSignedUrlResponse.url,
              file: renamedFile,
              file_type: renamedFile.type
            })

            // Process document on aws:
            await documentService.processDocumentAws({
              document_id: preSignedUrlResponse.document_id,
              document_name: renamedFile?.name,
              document_type: type,
              document_url: preSignedUrlResponse.key,
              created_by: doctor?.doctor_id,
              created_by_name: doctor?.name,
              patient_id: patient?.patient_id
            })

            uploadedDocIds.push(preSignedUrlResponse.document_id)
            // uploadedDocIds.push(`98-${type}-817`)

            toast.update(toastFile, {
              render: `${AmbulatoryDoctorDocument[type]} enviado com sucesso!`,
              type: 'success',
              isLoading: false,
              autoClose: 2000
            })
          } catch (error: any) {
            console.error(error?.message)
            toast.update(toastFile, {
              render: `Erro ao enviar ${AmbulatoryDoctorDocument[type]}!`,
              type: 'error',
              isLoading: false,
              autoClose: 3000
            })
            throw error
          }
        })
      )

      return uploadedDocIds
    }

    const mappedDocumentList = () => {
      const mappedDocs = state.documents?.map(
        (document: DocumentFile) => document || []
      )

      return documentList.map((docType: DocumentFile) => {
        const doc = mappedDocs?.find(
          (doc: DocumentFile) => doc.type === docType.type
        )
        return {
          ...docType,
          document_id: doc?.document_id,
          filename: doc?.filename,
          file: doc?.file,
          status: doc?.status
        }
      })
    }

    const handleChangeInformationAccordion = (
      label: string,
      isExpanded: boolean
    ) => {
      setInformationAccordionOpen(!isExpanded ? '' : label)
    }

    useEffect(() => {
      if (location.hash) {
        const element = document.getElementById(location.hash.replace('#', ''))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }, [location])

    return (
      <>
        <HeaderNew
          actualPageOnClick={() => history.goBack()}
          actualPageTitle="Solicitar pedido ambulatorial"
        />

        <ContainerNew
          title="Resumo do Pedido"
          titleColor="neutral800"
          titleSize="large"
          titleWeight="bold"
          primaryButton={
            <ButtonNew fullWidth size="large" onClick={handleSubmit}>
              Finalizar
            </ButtonNew>
          }
        >
          <S.Wrapper>
            <S.CardInfo>
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Dados do Paciente
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('solicitacao', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Paciente
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {state.patient?.name}
                  </SupportTextNew>
                </S.BoxInfo>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    CPF
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {state.patient?.cpf ? formatCpf(state.patient?.cpf) : ''}
                  </SupportTextNew>
                </S.BoxInfo>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Data de Nascimento
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {moment(state.patient?.birthday).format('DD/MM/YYYY')}
                  </SupportTextNew>
                </S.BoxInfo>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Celular
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {formatPhone(state.patient?.phone)}
                  </SupportTextNew>
                </S.BoxInfo>
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo>
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Convênio
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('convenio', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Convênio
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {state.health_insurance_info?.health_insurance_name}
                  </SupportTextNew>
                </S.BoxInfo>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Número da carteira
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {state.health_insurance_info?.health_card}
                  </SupportTextNew>
                </S.BoxInfo>
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="unid">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Unidade
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('unidade', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                <S.BoxInfo>
                  <HeadingNew
                    as="h6"
                    color="neutral900"
                    size="xxsmall"
                    weight="bold"
                  >
                    Unidade
                  </HeadingNew>
                  <SupportTextNew color="neutral700" size="xsmall">
                    {state.unit?.friendly_name}
                  </SupportTextNew>
                </S.BoxInfo>
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="proc">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Procedimentos
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('procedimentos', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                {state.procedures?.length ? (
                  state.procedures.map((procedure: Procedure) => (
                    <ProcedureCard key={procedure.code} procedure={procedure} />
                  ))
                ) : (
                  <CardNew
                    padding="24px"
                    background="neutral50"
                    shadow="none"
                    border="neutral200"
                  >
                    <SupportTextNew
                      color="neutral500"
                      size="small"
                      weight="semiBold"
                    >
                      Você não incluiu nenhum procedimento
                    </SupportTextNew>
                  </CardNew>
                )}
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="cid">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  CID
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('cids', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                {state.cids?.length ? (
                  state.cids.map((cid: Cid) => (
                    <CidCard key={cid.code} cid={cid} />
                  ))
                ) : (
                  <CardNew
                    padding="24px"
                    background="neutral50"
                    shadow="none"
                    border="neutral200"
                  >
                    <SupportTextNew
                      color="neutral500"
                      size="small"
                      weight="semiBold"
                    >
                      Você não incluiu nenhum CID
                    </SupportTextNew>
                  </CardNew>
                )}
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="med">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Medicações
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('medicacao', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                {state.medications?.length ? (
                  state.medications.map((medication: Medication) => (
                    <MedicationCard
                      medication={medication}
                      key={medication.code}
                    />
                  ))
                ) : (
                  <CardNew
                    padding="24px"
                    background="neutral50"
                    shadow="none"
                    border="neutral200"
                  >
                    <SupportTextNew
                      color="neutral500"
                      size="small"
                      weight="semiBold"
                    >
                      Você não incluiu nenhuma medicação
                    </SupportTextNew>
                  </CardNew>
                )}
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="mat">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Materiais
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('materiais', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                {state.materials?.length ? (
                  state.materials.map((material: Material, index: number) => (
                    <MaterialCard key={index} material={material} />
                  ))
                ) : (
                  <CardNew
                    padding="24px"
                    background="neutral50"
                    shadow="none"
                    border="neutral200"
                  >
                    <SupportTextNew
                      color="neutral500"
                      size="small"
                      weight="semiBold"
                    >
                      Você não incluiu nenhum material
                    </SupportTextNew>
                  </CardNew>
                )}
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="obs">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Observações
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('observacoes', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent>
                <TextAreaNew
                  value={state.observation || 'Nenhuma observação'}
                  rows={state.observation ? 4 : 1}
                  disabled
                />
              </S.CardContent>
            </S.CardInfo>

            <S.CardInfo id="docs">
              <S.CardHeader>
                <HeadingNew color="neutral800" size="small" weight="normal">
                  Documentos
                </HeadingNew>
                <ButtonNew
                  className="btn-edit"
                  variant="text"
                  fontSize="xsmall"
                  noPadding
                  onClick={() =>
                    history.push('documentos', {
                      isEditing: true
                    })
                  }
                >
                  <EditIcon width={18} height={18} />
                </ButtonNew>
              </S.CardHeader>
              <S.CardContent style={{ paddingBottom: '24px' }}>
                {mappedDocumentList()?.map(
                  (document: DocumentFile, index: number) => (
                    <AccordionCardNew
                      key={document.document_id ?? index}
                      header={`${document.label} ${
                        document.required ? '*' : ''
                      }`}
                      accordionLabel={document.label}
                      expandIcon={
                        document.file?.name || document?.filename ? (
                          <ArrowIcon width={16} height={16} />
                        ) : (
                          false
                        )
                      }
                      headerIcon={
                        document.status === 'ATTACHED' ? (
                          <CheckCircle />
                        ) : (
                          <AlertCircle />
                        )
                      }
                      expanded={
                        document.file?.name || document?.filename
                          ? informationAccordionOpen === document.label
                          : false
                      }
                      handleChange={handleChangeInformationAccordion}
                      padding="16px"
                      border="neutral100"
                      shadow="normal"
                    >
                      <MultimediaFileInputNew
                        name={`file-input-${document.type}`}
                        onChange={() => null}
                        fileName={
                          document.file?.name ?? document?.filename ?? ''
                        }
                        maxFileSizeMB={document.maxFileSizeInMB}
                        disabled
                      />
                    </AccordionCardNew>
                  )
                )}
              </S.CardContent>
            </S.CardInfo>
          </S.Wrapper>
        </ContainerNew>
      </>
    )
  }
)

export default AmbulatoryResumeLayout
